import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { ColDef, GridOptions, GridApi, RowSelectionOptions, SelectionColumnDef } from "ag-grid-community";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { CheckboxRendererComponent } from "../../../../common/components/ag-grid/checkbox-renderer.component";
import { AgGridBooleanFilterComponent } from "../../../../common/components/ag-grid/filters/boolean-filter.component";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { SubHeader } from "../../../../core/models/sub-header.model";
import { ContactHistoryModel, ContactHistoryCriteria } from "../../../models/admin-reports.model";
import { ReportArgs } from "../../../../common/models/reports.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { AdminReportsService } from '../../../services/admin-reports.service';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { DateService } from "../../../../common/services/date.service";
import { OrganizationService } from "../../../../common/services/organization.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { Option } from "../../../../common/models/flow.dto";
import { AgentTypeOptions } from "../../../models/contact.model";
import { ContactEventType } from "../../../models/contactHistory.model";
import { OrganizationStub } from "../../../../common/models/organization.model";




@Component({
    selector: 'contact-history-report',
    templateUrl: './contact-history-report.component.html',
    styleUrls: ['./contact-history-report.component.scss'],
    standalone: false
})
export class ContactHistoryReportComponent implements OnInit, OnDestroy {

    get agentTypes(): Option[] {
        return AgentTypeOptions.All;
    }

    get excelStyles(): any[] {
        if (this.formatService) return this.formatService.excelStyles;
        return [];
    }

    get booleanFilterParams(): any {
        let params: any = {};
        if (this.formatService) {
            params = this.formatService.defaultBooleanFilterParams;
            params.isInServerSideFilterMode = false;
        }
        return params;
    }

    get selectionColumnDef(): SelectionColumnDef {
        return this.formatService.defaultSelectionColumnDef;
    }

    data: ContactHistoryModel[] = [];
    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => { return; })]);
    formGroup: FormGroup = new FormGroup({});
    fromDate: Date | null;
    toDate: Date | null;
   

	gridOptions: GridOptions;
	gridApi?: GridApi;
    organizations: OrganizationStub[];
	organizationGridOptions: GridOptions;
	organizationGridApi?: GridApi;
    get organizationSelected(): boolean {
        let ctrl = this.formGroup.get("organization");
        let result: boolean = false;
        if (ctrl) {
            let value = ctrl.value;
            result = value && value.length > 0;
        }
        return result;
    }

    runText: string = "Run Report";

    subHeader: SubHeader = new SubHeader({ title: "Contact History Report", buttons: [] });

    subscription: Subscription = new Subscription();



    constructor(private readonly builder: FormBuilder,
        private readonly dataService: AdminReportsService,
        private readonly formatService: GridFormatterService,
        private readonly subheaderService: SubHeaderService,
        private readonly organizationService: OrganizationService,
        private readonly dialogService: DynamicDialogService,
        private readonly dateService: DateService, private readonly datePipe: DatePipe) {
    }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subHeader);
        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = new Date();
        this.formGroup = this.buildForm();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, true);
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
		this.organizationGridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(this.defineOrganizationGridColumns(), false, true, true, true);
		this.organizationGridOptions.onGridReady = (params) => { 
            this.organizationGridApi = params.api; 
            this.onGridReady(params);
        };
        this.organizationGridOptions.rowSelection = {
            mode: "singleRow"
        } as RowSelectionOptions;
        let orgs$ = this.organizationService.getExternalList().pipe(tap((orgs) => {
            this.organizations = orgs;
        })).subscribe({
            next: (success) => {
				if (this.organizationGridApi != null) {
                    this.organizationGridApi.setGridOption("rowData", this.organizations);
				    this.organizationGridApi.sizeColumnsToFit();
                    this.organizationGridApi.setGridOption("loading", false);
                }
                
            },
            error: (error) => {
                console.error(error);
                this.showError();
            }
        });
        this.subscription.add(orgs$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    buildForm(): FormGroup {
        const group: any = {};
        group["organization"] = new FormControl({ value: "", disabled: true }, [Validators.required]);
        group["fromDate"] = new FormControl(this.fromDate, [Validators.required]);
        group["toDate"] = new FormControl(this.toDate, [Validators.required]);
        this.agentTypes.forEach(a => {
            group[a.text] = new FormControl(false, []);
        });
        return this.builder.group(group);
    }

    createColumnDefinitions(): ColDef[] {
        return [
            {
                headerName: "Organization",
                headerTooltip: "Organization",
                field: "organizationName",
                tooltipField: "organizationName",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 150,
                maxWidth: 175
            } as ColDef,
            {
                headerName: "RICS Owner #",
                headerTooltip: "RICS Owner #",
                field: "ricsOrganizationId",
                tooltipField: "ricsOrganizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                maxWidth: 140,
                minWidth: 140
            } as ColDef,
            {
                headerName: "RBDMS Owner #",
                headerTooltip: "RBDMS Owner #",
                field: "legacyOwnerId",
                tooltipField: "legacyOwnerId",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                maxWidth: 140,
                minWidth: 140
            } as ColDef,
            {
                headerName: "Contact Name",
                headerTooltip: "Contact Name",
                field: "contactName",
                tooltipField: "contactName",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 125
            } as ColDef,
            {
                headerName: "Enabled?",
                headerTooltip: "Enabled?",
                field: "isEnabled",
                tooltipField: "isEnabled",
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "isEnabled",
                    description: "Enabled?",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                tooltipValueGetter: this.formatService.formatBooleanToYesNo("isEnabled"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this.booleanFilterParams,
                maxWidth: 125,
                minWidth: 125
            } as ColDef,
            {
                headerName: "Is User?",
                headerTooltip: "Is User?",
                field: "isUser",
                tooltipField: "isUser",
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "isUser",
                    description: "Is User?",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                tooltipValueGetter: this.formatService.formatBooleanToYesNo("isUser"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this.booleanFilterParams,
                maxWidth: 125,
                minWidth: 125
            } as ColDef,
            {
                headerName: "Event Type",
                headerTooltip: "Event Type",
                field: "eventType",
                valueGetter: this.formatEventType("eventType"),
                tooltipValueGetter: this.formatEventType("eventType"),
                minWidth: 200,
                maxWidth: 300
            } as ColDef,
            {
                headerName: "Event Details",
                headerTooltip: "Event Details",
                field: "eventDetails",
                tooltipField: "eventDetails",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 260,
                maxWidth: 350
            } as ColDef,
            {
                headerName: "Date / Time Stamp",
                headerTooltip: "Date / Time Stamp",
                field: "timestamp",
                tooltipField: "timestamp",
                valueFormatter: this.formatService.formatDate("timestamp", "MM/dd/yyyy hh:mm a"),
                tooltipValueGetter: this.formatService.formatDate("timestamp", "MM/dd/yyyy hh:mm a"),
                valueGetter: this.formatService.formatDate("timestamp"),
                filter: "agDateColumnFilter",
                filterParams: this.formatService.defaultDateFilterParams,
                sort: "desc",
                sortingOrder: ["asc", null, "desc"],
                comparator: dateComparator,
                minWidth: 225
            } as ColDef
        ] as ColDef[];
    }

    defineOrganizationGridColumns(): ColDef[] {
        return [
            {
                field: "id",
                headerName: "RICS Owner #",
                headerTooltip: "RICS Owner #",
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                tooltipField: "id",
                maxWidth: 175,
                resizable: true
            } as ColDef, {
                field: "historicalOwnerNumber",
                headerName: "RBDMS Owner #",
                valueGetter: this.getHistoricalNumber(this),
                tooltipValueGetter: this.getHistoricalNumber(this),
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                maxWidth: 175,
                resizable: true
            } as ColDef, 
            {
                field: "name",
                headerName: "Organization Name",
                headerTooltip: "Organization Name",
                tooltipField: "name",
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                resizable: true
            }


        ] as ColDef[];
    }

    downloadReport(args: ReportArgs): void {
        this.fromDate = args.fromDate;
        this.toDate = args.toDate;
        const organization = this.formGroup.get("organization").value;
        let fileName = `${organization}_ContactHistoryReport_${this.datePipe.transform(this.fromDate, "yyyy-MM-dd").toString()}_${this.datePipe.transform(this.toDate, "yyyy-MM-dd").toString()}`;
        let sheetName = "ContactHistory";
        this.formatService.exportDataToExcel(fileName, sheetName, this.gridApi);
    }

    generateReport(args: ReportArgs) : void {
        this.fromDate = args.fromDate;
        this.toDate = args.toDate;
        this.formGroup.get("fromDate").setValue(this.fromDate);
        this.formGroup.get("toDate").setValue(this.toDate);
        const criteria = new ContactHistoryCriteria();
        criteria.fromDate = this.fromDate;
        criteria.toDate = this.toDate;
        criteria.organizationId = this.formGroup.get("organization").value;
        criteria.authorities = [];
        criteria.events = Object.keys(ContactEventType).map(key => ContactEventType[key]).filter(value => typeof value === 'string') as string[]; //[ "AgentTypeChanged" ];
        for (let a = 0; a < this.agentTypes.length; a++) {
            let ctrl = this.formGroup.get(this.agentTypes[a].text);
            let val = (ctrl) ? Boolean(ctrl.value) : false;
            if (val) {
                criteria.authorities.push(parseInt(this.agentTypes[a].id));
            }
        }
        if (this.gridApi) this.gridApi.setGridOption("loading", true);
        if (this.formGroup.valid) {
            let reports$ = this.dataService.getContactHistoryReport(criteria).pipe(
                tap((results) => {
                    this.data = results;
                })
            ).subscribe({
                next: (success) => {
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.data);
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.sizeColumnsToFit();
                    }
                },
                error: (error) => {
                    console.error(error);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                    this.showError();
                }
            });
            this.subscription.add(reports$);
        };        
    }

    getHistoricalNumber(component: any) {
        return (obj: any) => {
            if (obj.data.historicalOwnerNumber) return obj.data.historicalOwnerNumber;
            if (obj.data.isLegacy) return obj.data.id;
            return "";
        };
    }

    public formatEventType(field: string): any {
        return (obj: any) => {
            var value = "N/A"
            if (obj.data) {
                let fieldValue = this.formatService.tryGetValue(obj.data, field);

                if (fieldValue != undefined && fieldValue != null)
                    value = this.formattedHistoryEventTypes()[obj.data.eventType];
            }
            return value;
        }
    }

    formattedHistoryEventTypes() {
        return ["Agent Type Changed", "Contact Creation", "Contact Can Login Changed" ];
    }

    onGridReady(event: any) {
        this.formatService.gridReadySizeToFit(event);
    }

    organizationChanged(event: Event): void {
        const tempSelected = [];
        if (this.organizationGridApi) {
            const rows = this.organizationGridApi.getSelectedRows();
            for (let r = 0; r < rows.length; r++) {
                tempSelected.push(rows[r] as OrganizationStub);
            }
        }
        if (tempSelected.length > 0) {
            this.formGroup.get("organization")?.setValue(tempSelected[0].id);
        } else {
            this.formGroup.get("organization")?.setValue("");
        }
    }

    resetOrganization(): void {
        if (this.organizationGridApi != null) {
            this.organizationGridApi.deselectAll();
        }
        this.formGroup.get("organization")?.setValue("");
		if (this.gridApi != null) {
            this.gridApi.setGridOption("rowData", []);
        }
    }

    showError(): void {
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }
}
